import type { TFunction } from 'i18next';
import {
  EVALUATION_REVIEWER_TYPE,
  EvaluationReviewer,
  type ReviewCycle,
  type ReviewForm,
} from '@gokrew/models/performance';
import { isTeamOnlyForm } from './teamFeedback';

/**
 * One reviewer per evaluation (spec 2026-10-29): the "Who fills it" choice
 * and the self/manager/peer booleans derived from it.
 */

/** The choices, in the order the create flow shows them. */
export const EVALUATION_REVIEWERS: EvaluationReviewer[] = [
  EvaluationReviewer.MANAGER,
  EvaluationReviewer.SELF,
  EvaluationReviewer.PEER,
  EvaluationReviewer.TEAM,
];

/** The reviewer and its derived booleans (exactly one true; none for the team). */
export const withReviewer = (reviewer: EvaluationReviewer) => ({
  reviewer,
  selfReviewEnabled: reviewer === EvaluationReviewer.SELF,
  managerReviewEnabled: reviewer === EvaluationReviewer.MANAGER,
  peerReviewEnabled: reviewer === EvaluationReviewer.PEER,
});

/** Whether the form asks this reviewer anything. */
export const formHasQuestionsFor = (
  form: ReviewForm | null | undefined,
  reviewer: EvaluationReviewer
) => {
  const type = EVALUATION_REVIEWER_TYPE[reviewer];
  return (form?.sections ?? []).some((s) =>
    s.questions.some((q) => q.reviewerTypes.includes(type))
  );
};

/** The reviewer a template implies, if any: a team-only form is the team's. */
export const reviewerForForm = (
  form: ReviewForm | null | undefined
): EvaluationReviewer | null =>
  isTeamOnlyForm(form) ? EvaluationReviewer.TEAM : null;

/** The short label key ("Autoévaluation"); several for an older cycle. */
export const reviewerLabelKey = (reviewer: EvaluationReviewer | null) =>
  reviewer
    ? `shared.evaluationReviewer.short.${reviewer}`
    : 'shared.evaluationReviewer.several';

type CycleDates = Pick<
  ReviewCycle,
  | 'reviewer'
  | 'selfReviewEnabled'
  | 'managerReviewEnabled'
  | 'peerReviewEnabled'
  | 'selfDueDate'
  | 'managerDueDate'
  | 'peerDueDate'
  | 'upwardDueDate'
  | 'periodStart'
  | 'periodEnd'
>;

/**
 * When the evaluation's reviewer must answer: that reviewer's due date. A
 * cycle from before one reviewer per evaluation (several reviewers) gives
 * its last due date.
 */
export const reviewerDueDate = (cycle: CycleDates): string | null => {
  switch (cycle.reviewer) {
    case EvaluationReviewer.MANAGER:
      return cycle.managerDueDate;
    case EvaluationReviewer.SELF:
      return cycle.selfDueDate;
    case EvaluationReviewer.PEER:
      return cycle.peerDueDate;
    case EvaluationReviewer.TEAM:
      return cycle.upwardDueDate;
    default: {
      const dates = [
        cycle.selfReviewEnabled && cycle.selfDueDate,
        cycle.managerReviewEnabled && cycle.managerDueDate,
        cycle.peerReviewEnabled && cycle.peerDueDate,
      ].filter((d): d is string => !!d);
      return dates.length ? dates.sort().at(-1)! : null;
    }
  }
};

/**
 * The "When" cell of a one-time evaluation in the Reviews list: the period
 * when there is one, then the reviewer's due date ("À remplir avant le
 * 12 nov. 2026"); "Not set" only when there is neither.
 */
export const evaluationWhenLines = (
  cycle: CycleDates,
  t: TFunction,
  formatDay: (value: string | null) => string
): string[] => {
  const { periodStart, periodEnd } = cycle;
  const due = reviewerDueDate(cycle);
  const lines = [
    periodStart || periodEnd
      ? t('common.dateRange', {
          start: formatDay(periodStart) || t('common.notSet'),
          end: formatDay(periodEnd) || t('common.notSet'),
        })
      : null,
    due ? t('evaluations.table.dueBy', { date: formatDay(due) }) : null,
  ].filter((line): line is string => !!line);
  return lines.length ? lines : [t('common.notSet')];
};
